import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  Alert,
  Slider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  CalendarToday,
  Description,
  Delete,
  Groups,
  Logout,
  Payments,
  PhotoCamera,
  Restaurant,
  Rule,
  Schedule,
  Send,
  Share,
  WhatsApp,
  ChatBubbleOutline,
  Work,
  VerifiedUser,
  VisibilityOff,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import TopAppBar from '../layout/TopAppBar';
import BottomNavigationBar from '../layout/BottomBar';
import Inputs from '../utilities/Inputs';
import SearchBar from '../utilities/SearchBar';
import InterestsSelection from '../utilities/InterestsOptions';
import {
  ActionButton,
  Chip,
  LocationCard,
  PageContainer,
  ParticipantCard,
  RulesList,
  SectionHeader,
  SurfaceCard,
  TableHero,
  TableMetaGrid,
} from '../ui/ConvivialComponents';

const heroImage = 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=1200&q=80';
const coverImage = 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=900&q=80';
const fallbackRuleOptions = [
  { id: 'discretion', title: 'Niente foto', text: 'Nessuna foto dei partecipanti senza consenso esplicito.', icon: 'no_photography' },
  { id: 'split_bill', title: 'Conto alla romana', text: 'Il conto viene diviso in modo equilibrato tra i partecipanti.', icon: 'payments' },
  { id: 'verified_only', title: 'Profili verificati', text: 'Il tavolo accetta solo profili completati e verificati.', icon: 'verified_user' },
  { id: 'punctuality', title: 'Puntualita', text: "Arrivo consigliato 10 minuti prima dell'orario fissato.", icon: 'schedule' },
  { id: 'professional_focus', title: 'Focus professionale', text: 'Conversazione orientata a networking, progetti e scambio professionale.', icon: 'work' },
];

export function TableCreateView({
  tableTheme,
  setTableTheme,
  tableStartAt,
  setTableStartAt,
  tableLocationName,
  handleSearchSelection,
  centerFromLocalStorage,
  description,
  setDescription,
  interests,
  setInterests,
  selectedRuleIds,
  setSelectedRuleIds,
  tableRuleOptions,
  coverImageUrl,
  setCoverImageUrl,
  setCoverFile,
  setCoverSource,
  numberParticipants,
  setNumberParticipants,
  handleCreateTable,
  handleUpdateTable,
  handleShareTable,
  isLoading,
  errorMessage,
  viewState,
  canEditSchedule = true,
  canDeleteTable = false,
  handleCancelTable,
  attendeeCount = 0,
  participants = [],
  userId,
  handleOpenChat,
}) {
  const isEdit = viewState === 'edit';
  const ruleOptions = tableRuleOptions?.length ? tableRuleOptions : fallbackRuleOptions;
  const activeRuleIds = Array.isArray(selectedRuleIds) ? selectedRuleIds : [];
  const selectedCover = coverImageUrl || coverImage;

  const toggleRule = (ruleId) => {
    setSelectedRuleIds((prev) => {
      const next = Array.isArray(prev) ? prev : [];
      return next.includes(ruleId)
        ? next.filter((id) => id !== ruleId)
        : [...next, ruleId];
    });
  };

  const handleCoverChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setCoverFile(file);
    setCoverSource('custom');
    setCoverImageUrl(URL.createObjectURL(file));
  };

  return (
    <>
      <TopAppBar title="ConvivialApp.it" showAvatar onShare={isEdit ? handleShareTable : undefined} />
      <PageContainer sx={{ marginTop: 'var(--cv-top-bar-height)', }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h1" sx={{ color: 'primary.main', fontSize: { xs: 28, md: 34 } }}>
              {isEdit ? 'Modifica Tavolo' : 'Crea un Tavolo'}
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 760 }}>
              L'Arte della Tavola comincia da qui. Definisci i dettagli del tuo prossimo incontro professionale conviviale.
            </Typography>
          </Box>

          <Box
            component="label"
            sx={{
              position: 'relative',
              display: 'block',
              height: { xs: 190, md: 240 },
              borderRadius: 3,
              overflow: 'hidden',
              boxShadow: '0 4px 20px rgba(29,29,27,0.05)',
              cursor: 'pointer',
            }}
          >
            <Box component="img" src={selectedCover} alt="Foto di copertina tavolo" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.62), rgba(0,0,0,0.05))', display: 'flex', alignItems: 'flex-end', p: 2.5 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ color: '#fff' }}>
                <PhotoCamera />
                <Typography variant="button" sx={{ color: '#fff' }}>Cambia foto copertina</Typography>
              </Stack>
            </Box>
            <Box component="input" type="file" accept="image/*" onChange={handleCoverChange} sx={{ display: 'none' }} />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '7fr 5fr' }, gap: 3, alignItems: 'start' }}>
            <Stack spacing={3}>
              <SurfaceCard>
                <Stack spacing={2}>
                  <Inputs
                    type="text"
                    label="Titolo del Tavolo"
                    value={tableTheme}
                    onChange={(e) => setTableTheme(e.target.value)}
                    required
                    mt={0}
                    mb={0}
                  />
                  <Inputs
                    type="date"
                    label="Data e orario"
                    value={tableStartAt}
                    onChange={(newDate) => setTableStartAt(newDate)}
                    required
                    disablePast={!isEdit}
                    disabled={!canEditSchedule}
                    mt={0}
                    mb={0}
                  />
                  <Box>
                    <Typography variant="button" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>
                      Località / Ristorante
                    </Typography>
                    <SearchBar
                      onSearchSelection={handleSearchSelection}
                      textInside={tableLocationName || ''}
                      center={centerFromLocalStorage()}
                      disabled={!canEditSchedule}
                    />
                    {!canEditSchedule && (
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
                        Ci sono gia altri partecipanti: luogo e orario restano bloccati per non spostare l'appuntamento.
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </SurfaceCard>

              <SurfaceCard>
                <Typography variant="button" sx={{ color: 'text.secondary' }}>
                  Tema e Descrizione
                </Typography>
                <TextField
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  multiline
                  minRows={5}
                  fullWidth
                  placeholder="Descrivi l'atmosfera, gli argomenti di discussione e il tono della serata..."
                  sx={{ mt: 1.25 }}
                />
                <Box sx={{ mt: 2 }}>
                  <InterestsSelection
                    viewState={isEdit ? 'edit' : 'edit'}
                    userInterests={Array.isArray(interests) ? interests : [interests].filter(Boolean)}
                    setUserInterests={setInterests}
                    selectOption
                  />
                </Box>
              </SurfaceCard>
            </Stack>

            <Stack spacing={3}>
              <SurfaceCard>
                <Typography variant="button" sx={{ color: 'text.secondary' }}>
                  Posti disponibili
                </Typography>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 1 }}>
                  <Slider
                    value={numberParticipants}
                    min={3}
                    max={8}
                    onChange={(event, value) => setNumberParticipants(value)}
                    sx={{ color: 'primary.main' }}
                  />
                  <Typography variant="h3" sx={{ color: 'primary.main', minWidth: 36, textAlign: 'center' }}>
                    {numberParticipants}
                  </Typography>
                </Stack>
              </SurfaceCard>

              {isEdit && (
                <ParticipantsSection participants={participants} userId={userId}
                  isUserAttending isCreator attendeeCount={attendeeCount} handleOpenChat={handleOpenChat} />
              )}

              <SurfaceCard>
                <SectionHeader icon={<Rule />} title="Regole della Tavola" />
                <Stack spacing={1.25}>
                  {ruleOptions.map((rule) => (
                    <RuleToggle
                      key={rule.id}
                      icon={ruleIcon(rule.icon)}
                      label={rule.title}
                      helperText={rule.text}
                      checked={activeRuleIds.includes(rule.id)}
                      onChange={() => toggleRule(rule.id)}
                    />
                  ))}
                </Stack>
              </SurfaceCard>
            </Stack>
          </Box>

          {errorMessage && <Typography color="error">{errorMessage}</Typography>}
          {/* Keep every table action above the mobile navigation, including deletion. */}
          <Stack spacing={1.25} sx={{ position: { xs: 'sticky', md: 'static' }, bottom: 'calc(var(--cv-bottom-nav-height) + 12px)', zIndex: 3, width: '100%', maxWidth: 520, alignSelf: 'center' }}>
            {isEdit ? (
              <Stack direction="row" spacing={1.25} alignItems="stretch" sx={{ maxWidth: 520, mx: 'auto' }}>
                <ActionButton
                  variant="container"
                  onClick={handleUpdateTable}
                  disabled={isLoading}
                  icon={<Restaurant />}
                  sx={{ flex: 1, minWidth: 0 }}
                >
                  Aggiorna Tavolo
                </ActionButton>
                <Button
                  variant="outlined"
                  onClick={handleShareTable}
                  disabled={isLoading}
                  aria-label="Condividi tavolo"
                  sx={{ width: 56, minWidth: 56, borderRadius: 3, flexShrink: 0, bgcolor: 'background.paper' }}
                >
                  <Share />
                </Button>
              </Stack>
            ) : (
              <ActionButton
                variant="container"
                onClick={handleCreateTable}
                disabled={isLoading}
                icon={<Restaurant />}
                sx={{ maxWidth: 520, mx: 'auto' }}
              >
                Pubblica Tavolo
              </ActionButton>
            )}
            {canDeleteTable && (
              <Button
                color="error"
                variant="outlined"
                onClick={handleCancelTable}
                disabled={isLoading}
                startIcon={<Delete />}
                sx={{ minHeight: 44, borderRadius: 2.5, fontWeight: 600, bgcolor: 'background.paper' }}
              >
                Elimina
              </Button>
            )}
          </Stack>

          {isEdit && attendeeCount > 1 && (
            <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center', display: 'block', mt: 1 }}>
              Puoi modificare tema, descrizione, interessi, regole e posti, ma non cancellare un tavolo con partecipanti.
            </Typography>
          )}

          <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
            Per organizzarti con i commensali usa la chat interna. Il tuo numero di telefono resta privato.
          </Alert>

        </Stack>
      </PageContainer>
      <BottomNavigationBar valueToHighlight={2} />
    </>
  );
}

function ruleIcon(icon) {
  const icons = {
    no_photography: <VisibilityOff />,
    payments: <Payments />,
    verified_user: <VerifiedUser />,
    schedule: <Schedule />,
    work: <Work />,
  };
  return icons[icon] || <Rule />;
}

function getSelectedRules(tableRuleOptions = [], selectedRuleIds = []) {
  const options = tableRuleOptions.length ? tableRuleOptions : fallbackRuleOptions;
  const ids = Array.isArray(selectedRuleIds) ? selectedRuleIds : [];
  return options
    .filter((rule) => ids.includes(rule.id))
    .map((rule) => ({ title: rule.title, text: rule.text }));
}

function RuleToggle({ icon, label, helperText, checked, onChange }) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 1.5, border: '1px solid', borderColor: 'outline.variant', borderRadius: 2, bgcolor: 'background.default' }}>
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ color: 'primary.main' }}>
        {icon}
        <Box>
          <Typography variant="button" sx={{ color: 'text.primary' }}>{label}</Typography>
          {helperText && (
            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', lineHeight: 1.35 }}>
              {helperText}
            </Typography>
          )}
        </Box>
      </Stack>
      <Checkbox checked={checked} onChange={onChange} sx={{ color: 'primary.main', '&.Mui-checked': { color: 'primary.main' } }} />
    </Stack>
  );
}

export function TableDetailView(props) {
  const {
    tableTheme,
    tableStartAt,
    tableLocationName,
    latLonPlace,
    description,
    interests,
    selectedRuleIds,
    tableRuleOptions,
    coverImageUrl,
    participants,
    attendeeCount,
    numberParticipants,
    status,
    userId,
    isUserAttending,
    isCreator,
    handleJoinTable,
    handleOpenWaitlist,
    handleShareTable,
    handleReportTable,
    handleCancelAttendee,
    selectedLeaveReasons,
    setSelectedLeaveReasons,
    leaveReasonOptions,
    isLoading,
    errorMessage,
    requestState,
    onLoginRequired,
  } = props;
  const navigate = useNavigate();

  if (status === 'terminato' && isUserAttending) return <TableEndedView {...props} />;
  if (status === 'in corso') return <TableLiveView {...props} />;

  const start = tableStartAt ? dayjs(tableStartAt) : null;
  const occupied = Number(attendeeCount || participants?.length || 0);
  const remaining = Math.max(Number(numberParticipants || 0) - occupied, 0);
  const host = participants?.find((p) => p.user_role === 'CREATOR') || participants?.[0];
  const selectedRules = getSelectedRules(tableRuleOptions, selectedRuleIds);

  return (
    <>
      <TopAppBar title="ConvivialApp.it" variant="back" showAvatar={!!userId} onShare={handleShareTable} />
      <PageContainer maxWidth="900px" sx={{ pt: 'calc(var(--cv-top-bar-height) + 24px)' }}>
        <Stack spacing={3}>
          <TableHero
            title={tableTheme || 'Tavolo Conviviale'}
            image={coverImageUrl || heroImage}
            eyebrow={Array.isArray(interests) ? interests[0] : interests}
            date={start ? start.format('DD MMM, HH:mm') : 'Data da confermare'}
            location={latLonPlace?.place || tableLocationName || 'Location da confermare'}
            seats={`${remaining} rimasti`}
            compact
          />

          <SurfaceCard sx={{ mt: { xs: -5, md: -6 }, position: 'relative', zIndex: 2 }}>
            <Typography variant="h2" sx={{ color: 'primary.main', mb: 2 }}>
              {tableTheme || 'Tavolo Conviviale'}
            </Typography>
            <TableMetaGrid
              items={[
                { label: 'Data', value: start ? start.format('DD MMM') : '-', icon: <CalendarToday fontSize="small" /> },
                { label: 'Ora', value: start ? start.format('HH:mm') : '-', icon: <CalendarToday fontSize="small" /> },
                { label: 'Luogo', value: latLonPlace?.place || 'Da confermare', icon: <Restaurant fontSize="small" /> },
                { label: 'Posti', value: `${remaining} rimasti`, icon: <Groups fontSize="small" />, color: 'secondary.main' },
              ]}
            />
            {host && (
              <>
                <Divider sx={{ my: 2.5 }} />
                <ParticipantCard
                  name={host.user_complete_name}
                  role="Organizzatore"
                  avatar={host.user_avatar_url}
                  isHost
                  onClick={() => navigate(`/user/${host.user_id}`)}
                  sx={{ border: 'none', bgcolor: 'transparent', p: 0 }}
                />
              </>
            )}
          </SurfaceCard>

          <SurfaceCard>
            <SectionHeader icon={<Description />} title="Tema e Descrizione" />
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              {description || 'Nessuna descrizione fornita dall’host.'}
            </Typography>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 2 }}>
              {(Array.isArray(interests) ? interests : String(interests || '').split(',')).filter(Boolean).map((interest) => (
                <Chip key={interest}>{String(interest).trim()}</Chip>
              ))}
            </Stack>
          </SurfaceCard>

          <ParticipantsSection handleOpenChat={props.handleOpenChat}
            participants={participants}
            userId={userId}
            isUserAttending={isUserAttending}
            isCreator={isCreator}
            onLoginRequired={onLoginRequired}
            attendeeCount={occupied}
          />

          <LocationCard
            title={latLonPlace?.place}
            address={latLonPlace?.directions || tableLocationName}
            lat={latLonPlace?.lat}
            lng={latLonPlace?.lng}
            onDirections={() => {
              if (latLonPlace?.directions) window.location.href = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(latLonPlace.directions)}`;
            }}
          />

          <SurfaceCard>
            <SectionHeader icon={<Rule />} title="Regole del tavolo" />
            <RulesList rules={selectedRules} />
          </SurfaceCard>

          {isUserAttending && !isCreator && (
            <SurfaceCard>
              <LeaveTableSection
                isLoading={isLoading}
                onCancel={handleCancelAttendee}
                selectedLeaveReasons={selectedLeaveReasons}
                setSelectedLeaveReasons={setSelectedLeaveReasons}
                leaveReasonOptions={leaveReasonOptions}
              />
            </SurfaceCard>
          )}

          <Box>
            <Button
              variant="outlined"
              color="warning"
              onClick={handleReportTable}
              sx={{ borderRadius: 3, fontWeight: 600, width: '100%', minHeight: 44 }}
            >
              Segnala
            </Button>
            {!isUserAttending && userId && (
              <Alert severity="info" sx={{ mt: 1.5, borderRadius: 2 }}>
                Dopo la conferma potrai scrivere nella chat del tavolo.
              </Alert>
            )}
          </Box>

          {errorMessage && <Typography color="error">{errorMessage}</Typography>}
          <ActionArea
            userId={userId}
            isUserAttending={isUserAttending}
            remaining={remaining}
            requestState={requestState}
            isLoading={isLoading}
            onLoginRequired={onLoginRequired}
            onJoin={handleJoinTable}
            isClosed={status === 'terminato'}
            onWaitlist={handleOpenWaitlist}
            onShare={handleShareTable}
          />
        </Stack>
      </PageContainer>
      <BottomNavigationBar valueToHighlight={2} />
    </>
  );
}

function ParticipantsSection({ participants = [], userId, isUserAttending = false, isCreator = false, handleOpenChat, onLoginRequired, attendeeCount = participants.length, onFeedback }) {
  const navigate = useNavigate();
  return (
    <SurfaceCard>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <SectionHeader icon={<Groups />} title="I commensali" sx={{ mb: 0 }} />
        <Typography variant="button" sx={{ color: 'secondary.main' }}>{attendeeCount} confermati</Typography>
      </Stack>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.5 }}>
        {participants.filter((attendee) => userId || attendee.user_role === 'CREATOR' || attendee.role === 'CREATOR').map((attendee, index) => {
          const attendeeId = attendee.user_id || attendee.userId;
          const isHost = attendee.user_role === 'CREATOR' || attendee.role === 'CREATOR';
          return (
            <Box key={attendeeId || index} sx={{ position: 'relative' }}>
              <ParticipantCard
                name={attendee.user_complete_name}
                role={isHost ? 'Organizzatore' : 'Partecipante'}
                avatar={attendee.user_avatar_url}
                isHost={isHost}
                onClick={() => attendeeId && navigate(`/user/${attendeeId}`)}
              />
              {onFeedback && attendee.user_id !== userId && (
                <Button size="small" variant="outlined" onClick={() => onFeedback(attendee.user_id)} sx={{ mt: 1, borderRadius: 2, mb: 2 }}>
                  Feedback
                </Button>
              )}
            </Box>
          );
        })}
        {Number(attendeeCount || 0) === 0 && (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>Nessun partecipante ancora confermato.</Typography>
        )}
      </Box>
      {userId && (isUserAttending || isCreator) && participants.some((person) =>
        person.user_role !== 'CREATOR' && person.role !== 'CREATOR') && handleOpenChat && (
        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #eadbd9' }}>
          <SectionHeader icon={<ChatBubbleOutline />} title="La chat del tavolo" sx={{ mb: 1 }} />
          <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 1.5 }}>
            Saluta i commensali e organizzatevi qui, direttamente su Convivial.
          </Typography>
          <ActionButton icon={<ChatBubbleOutline />} onClick={handleOpenChat}>Apri la chat del tavolo</ActionButton>
        </Box>
      )}
      {!userId && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Accedi a Convivial per vedere tutti i commensali.
          </Typography>
          {!userId && <ActionButton onClick={onLoginRequired} sx={{ mt: 1.5 }}>Accedi per partecipare</ActionButton>}
        </Box>
      )}
    </SurfaceCard>
  );
}


function LeaveTableSection({
  isLoading,
  onCancel,
  selectedLeaveReasons = [],
  setSelectedLeaveReasons,
  leaveReasonOptions = [],
}) {
  const toggleLeaveReason = (reasonId) => {
    if (!setSelectedLeaveReasons) return;
    setSelectedLeaveReasons((prev) => {
      const next = Array.isArray(prev) ? prev : [];
      return next.includes(reasonId)
        ? next.filter((id) => id !== reasonId)
        : [...next, reasonId];
    });
  };

  return (
    <Box>
      <Typography variant="button" sx={{ color: 'text.secondary' }}>
        Vuoi lasciare il tavolo?
      </Typography>
      <Stack spacing={0.5} sx={{ mt: 1 }}>
        {leaveReasonOptions.map((reason) => (
          <Stack key={reason.id} direction="row" alignItems="center" spacing={1}>
            <Checkbox
              checked={selectedLeaveReasons.includes(reason.id)}
              onChange={() => toggleLeaveReason(reason.id)}
              sx={{ color: 'primary.main', '&.Mui-checked': { color: 'primary.main' } }}
            />
            <Typography variant="body2">{reason.label}</Typography>
          </Stack>
        ))}
      </Stack>
      <Button
        variant="outlined"
        color="error"
        onClick={() => onCancel(selectedLeaveReasons)}
        disabled={isLoading || selectedLeaveReasons.length === 0}
        startIcon={<Logout />}
        sx={{ mt: 1.5, borderRadius: 3, fontWeight: 600, width: '100%', minHeight: 44 }}
      >
        Lascia il tavolo
      </Button>
    </Box>
  );
}

function ActionArea({
  userId,
  isUserAttending,
  remaining,
  requestState,
  isLoading,
  onLoginRequired,
  onJoin,
  onWaitlist,
  onShare,
  isClosed = false,
}) {
  const isFull = remaining <= 0 && !isUserAttending;
  let primaryLabel = 'Chiedi di partecipare';
  if (!userId) primaryLabel = 'Accedi per partecipare';
  if (requestState === 'pending') primaryLabel = 'Richiesta in attesa';
  if (requestState === 'accepted' || isUserAttending) primaryLabel = 'Partecipazione confermata';
  if (isFull && userId) primaryLabel = "Lista d'attesa";

  if (isClosed && userId) primaryLabel = 'Tavolo concluso';
  const isWhatsApp = isFull && userId;

  const handlePrimaryClick = () => {
    if (!userId) {
      onLoginRequired();
      return;
    }
    if (isFull) {
      onWaitlist();
      return;
    }

    onJoin();
  };

  return (
    <SurfaceCard sx={{ position: 'sticky', bottom: 'calc(var(--cv-bottom-nav-height) + 10px)', zIndex: 4, p: 2 }}>
      <Stack direction="row" spacing={1.25} alignItems="stretch">
        <ActionButton
          disabled={isLoading || requestState === 'pending' || isUserAttending || (isClosed && !!userId)}
          onClick={handlePrimaryClick}
          icon={isWhatsApp ? <WhatsApp /> : <Send />}
          sx={{
            flex: 1,
            minWidth: 0,
            ...(isWhatsApp && {
              bgcolor: '#25D366',
              color: '#fff',
              '&:hover': { bgcolor: '#1DA851' },
              '&.Mui-disabled': { bgcolor: '#25D366', color: '#fff', opacity: 0.65 },
            }),
          }}
        >
          {primaryLabel}
        </ActionButton>
        <Button
          variant="outlined"
          onClick={onShare}
          aria-label="Condividi tavolo"
          sx={{ width: 56, minWidth: 56, borderRadius: 3, flexShrink: 0 }}
        >
          <Share />
        </Button>
      </Stack>
      {requestState === 'pending' && (
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 1 }}>
          Stiamo confermando la tua partecipazione.
        </Typography>
      )}
    </SurfaceCard>
  );
}

export function TableLiveView(props) {
  const {
    tableTheme,
    tableStartAt,
    tableLocationName,
    latLonPlace,
    description,
    interests,
    participants,
    attendeeCount,
    numberParticipants,
    userId,
    isUserAttending,
    isCreator,
    onLoginRequired,
    handleShareTable,
    selectedRuleIds,
    tableRuleOptions,
    coverImageUrl,
  } = props;
  const start = tableStartAt ? dayjs(tableStartAt) : null;
  const selectedRules = getSelectedRules(tableRuleOptions, selectedRuleIds);
  const occupied = Number(attendeeCount || participants.length || 0);

  return (
    <>
      <TopAppBar title="ConvivialApp.it" showAvatar={!!userId} onShare={handleShareTable} />
      <PageContainer maxWidth="900px" sx={{ pt: 'calc(var(--cv-top-bar-height) + 24px)' }}>
        <Stack spacing={3}>
          <TableHero
            title={tableTheme || 'Tavolo Live'}
            image={coverImageUrl || heroImage}
            statusLabel="LIVE NOW"
            eyebrow={latLonPlace?.place || 'Tavolo in corso'}
            date={start ? start.format('HH:mm') : 'Ora da confermare'}
            location={latLonPlace?.place || tableLocationName}
            seats={`${occupied} / ${numberParticipants} posti`}
          />
          {!isUserAttending && (
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              Questo tavolo sta per iniziare o è già in corso: non puoi più unirti da Convivial, ma puoi contattare il locale se vuoi verificare disponibilità.
            </Alert>
          )}
          <SurfaceCard>
            <SectionHeader icon={<Description />} title="Tema e Descrizione" />
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              {description || 'Il tavolo è in corso. Coordinati con gli altri partecipanti nella chat del tavolo.'}
            </Typography>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 2 }}>
              {(Array.isArray(interests) ? interests : String(interests || '').split(',')).filter(Boolean).map((interest) => (
                <Chip key={interest}>{String(interest).trim()}</Chip>
              ))}
            </Stack>
          </SurfaceCard>
          <ParticipantsSection handleOpenChat={props.handleOpenChat}
            participants={participants}
            userId={userId}
            isUserAttending={isUserAttending}
            isCreator={isCreator}
            onLoginRequired={onLoginRequired}
            attendeeCount={occupied}
          />
          <LocationCard
            title={latLonPlace?.place}
            address={latLonPlace?.directions || tableLocationName}
            lat={latLonPlace?.lat}
            lng={latLonPlace?.lng}
          />
          <SurfaceCard>
            <SectionHeader icon={<Rule />} title="Regole del tavolo" />
            <RulesList rules={selectedRules} />
          </SurfaceCard>
        </Stack>
      </PageContainer>
      <BottomNavigationBar valueToHighlight={2} />
    </>
  );
}

export function TableEndedView(props) {
  const { tableTheme, tableLocationName, latLonPlace, participants, userId, handleShareTable, groupId, navigateToFeedback } = props;

  return (
    <>
      <TopAppBar title="ConvivialApp.it" showAvatar={!!userId} onShare={handleShareTable} />
      <PageContainer sx={{ marginTop: 'var(--cv-top-bar-height)', }}>
        <Stack spacing={3}>
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Box sx={{ width: 80, height: 80, borderRadius: '50%', bgcolor: 'primary.container', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 10px 24px rgba(97,0,31,0.18)', mb: 2 }}>
              <VerifiedUser sx={{ fontSize: 42 }} />
            </Box>
            <Typography variant="h2" sx={{ color: 'primary.main' }}>Grazie per aver partecipato</Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 520, mx: 'auto' }}>
              Speriamo che la serata sia stata ricca di connessioni professionali e sapori indimenticabili.
            </Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '8fr 4fr' }, gap: 3 }}>
            <SurfaceCard>
              <Chip selected>Evento Concluso</Chip>
              <Typography variant="h1" sx={{ color: 'text.primary', mt: 1.5, fontSize: { xs: 30, md: 40 } }}>{tableTheme}</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {latLonPlace?.place || tableLocationName}
              </Typography>
              <Divider sx={{ my: 3 }} />
              <Typography variant="button" sx={{ color: 'primary.main' }}>Il tuo feedback</Typography>
              <Typography variant="body1" sx={{ mt: 1, mb: 3 }}>
                La tua opinione aiuta la community a crescere. Valuta la qualità della conversazione e l'esperienza gastronomica.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <ActionButton onClick={() => navigateToFeedback(groupId)}>Lascia un Feedback</ActionButton>
                <ActionButton variant="secondary" icon={<Share />} onClick={handleShareTable}>Condividi Esperienza</ActionButton>
              </Stack>
            </SurfaceCard>
            <ParticipantsSection handleOpenChat={props.handleOpenChat} participants={participants} userId={userId}
              isUserAttending={props.isUserAttending} isCreator={props.isCreator}
              attendeeCount={props.attendeeCount} onFeedback={() => navigateToFeedback(groupId)} />
          </Box>
          <SurfaceCard sx={{ bgcolor: 'primary.main', color: '#fff' }}>
            <Typography variant="h3" sx={{ color: '#fff' }}>Ti e piaciuta l'esperienza?</Typography>
            <Typography sx={{ opacity: 0.9, mb: 2 }}>Scopri altri tavoli esclusivi in programma nella tua zona.</Typography>
            <ActionButton variant="quiet">Esplora Tavoli</ActionButton>
          </SurfaceCard>
        </Stack>
      </PageContainer>
      <BottomNavigationBar valueToHighlight={2} />
    </>
  );
}
